/**
 * PLW Klaviyo signup form — subscribes an email to a Klaviyo list via the
 * client Subscriptions API, mirroring the playlovewin.com teaser page.
 *
 * Wraps a plain <form> with an email input and a submit button:
 *
 *   <plw-klaviyo-form data-company-id data-list-id data-source>
 *     <form> … <input type="email" required> … </form>
 *     <p data-status="success" hidden>…</p>
 *     <p data-status="error" hidden>…</p>
 *   </plw-klaviyo-form>
 *
 * Status copy is server-rendered in Liquid (translated); this module only
 * toggles `hidden`. Native HTML5 validation gates submission (the submit
 * event doesn't fire for an invalid required email field). With JS off the
 * form is inert — Klaviyo is the only destination (no Shopify customer POST).
 * The module loads once per page (script[type=module] URLs dedupe).
 */

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const KLAVIYO_REVISION = '2026-04-15';

class PlwKlaviyoForm extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLFormElement | null} */
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#handleSubmit);
    this.abortController?.abort();
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.form) return;
    const input = this.form.querySelector('input[type="email"]');
    const button = this.form.querySelector('button[type="submit"]');
    if (!(input instanceof HTMLInputElement)) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.#setStatus(null);
    if (button) button.disabled = true;

    try {
      const response = await fetch(
        `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.companyId || '')}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify(this.#payload(input.value)),
          signal: this.abortController.signal,
        }
      );
      if (!response.ok) throw new Error(`Klaviyo subscription failed (${response.status})`);
      input.value = '';
      this.#setStatus('success');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      this.#setStatus('error');
      input.focus();
    } finally {
      if (button) button.disabled = false;
    }
  };

  /** @param {string} email */
  #payload(email) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.dataset.source || 'Website',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: this.dataset.listId || '' } } },
      },
    };
  }

  /** Show one status element (or none) — copy lives in the Liquid. @param {'success' | 'error' | null} state */
  #setStatus(state) {
    for (const status of this.querySelectorAll('[data-status]')) {
      status.hidden = status.dataset.status !== state;
    }
  }
}

if (!customElements.get('plw-klaviyo-form')) {
  customElements.define('plw-klaviyo-form', PlwKlaviyoForm);
}
